import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { apiError } from "@/lib/api";
import { useCheckout } from "@/hooks/usePayments";
import { usePublicSettings } from "@/hooks/useSettings";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, Crown, Coins, Building2, Zap } from "lucide-react";

const LEARNER = ["Pay only for what you learn", "2-min, 15-min & full courses", "Lifetime access to your library"];
const SCHOLAR = ["Everything in Learner", "Unlimited course generation", "Personalpedia knowledge base", "Personal FAQ + pinned answers"];
const ENTERPRISE = ["Everything in Scholar", "Team account management", "Curated course catalog", "White-label branding", "Compliance tracking & certificates"];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: settings } = usePublicSettings();
  const bundles = settings?.coin_packages || [];

  const checkout = useCheckout({
    onSuccess: (data) => { window.location.href = data.url; },
    onError: (e) => toast.error(apiError(e)),
  });

  const buy = (packageId) => {
    if (!user) { navigate("/register"); return; }
    checkout.mutate(packageId);
  };

  const isScholar = user?.plan_type === "scholar";

  const ratio = settings?.coin_usd_ratio || 5;
  const costs = settings?.service_costs || {};
  const COURSE_ROWS = [
    { key: "overview", label: "2-min Overview" },
    { key: "lunch", label: "15-min Lunch Course" },
    { key: "comprehensive", label: "Full Training" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-6xl mx-auto px-6 md:px-8 py-16">
        <div className="text-center mb-14">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-2">Pricing</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter">Three ways to learn.</h1>
          <p className="text-muted-foreground mt-3 max-w-lg mx-auto">Pay as you go, subscribe for unlimited, or scale your whole team.</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 items-start">
          {/* Learner */}
          <div className="border border-border bg-card p-8" data-testid="tier-learner">
            <div className="flex items-center gap-2 mb-1"><Zap className="h-5 w-5 text-primary" /><h3 className="font-display text-2xl font-bold">Learner</h3></div>
            <p className="text-sm text-muted-foreground">Pay-as-you-go credits</p>
            <div className="mt-4 mb-6"><span className="font-display text-4xl font-extrabold">Refill wallet</span></div>
            <ul className="space-y-3 mb-6">
              {LEARNER.map((f) => <li key={f} className="flex items-center gap-3 text-sm"><Check className="h-4 w-4 text-primary shrink-0" /> {f}</li>)}
            </ul>

            <div className="border border-border p-4 mb-6" data-testid="course-cost-table">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">What each course costs</p>
              <div className="space-y-2">
                {COURSE_ROWS.map((row) => {
                  const c = costs[row.key];
                  const cash = c != null ? (c / ratio).toFixed(2) : "—";
                  return (
                    <div key={row.key} className="flex items-center justify-between text-sm" data-testid={`course-cost-${row.key}`}>
                      <span>{row.label}</span>
                      <span className="font-semibold inline-flex items-center gap-1.5">
                        <Coins className="h-3.5 w-3.5 text-primary" />{c != null ? c : "—"}
                        <span className="text-muted-foreground font-normal">(${cash})</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Refill your wallet</p>
            <div className="space-y-2">
              {bundles.map((b) => (
                <button key={b.id} onClick={() => buy(b.id)} disabled={checkout.isPending}
                  className="w-full flex items-center justify-between border border-primary p-4 text-sm font-semibold transition-colors hover:bg-primary/5"
                  data-testid={`buy-${b.id}`}>
                  <span className="inline-flex items-center gap-2"><Coins className="h-4 w-4 text-primary" /> {b.coins} coins</span>
                  <span className="font-display text-lg">${b.usd.toFixed(2)}</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">A $10 top-up adds coins at the current rate ({ratio} coins per $1).</p>
          </div>

          {/* Scholar */}
          <div className="border-2 border-primary bg-card p-8 relative" data-testid="tier-scholar">
            <span className="absolute -top-3 left-8 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider px-3 py-1">Most popular</span>
            <div className="flex items-center gap-2 mb-1"><Crown className="h-5 w-5 text-primary" /><h3 className="font-display text-2xl font-bold">Scholar</h3></div>
            <p className="text-sm text-muted-foreground">Unlimited monthly membership</p>
            <div className="mt-4 mb-6"><span className="font-display text-5xl font-extrabold">$19</span><span className="text-muted-foreground">/mo</span></div>
            <ul className="space-y-3 mb-8">
              {SCHOLAR.map((f) => <li key={f} className="flex items-center gap-3 text-sm"><Check className="h-4 w-4 text-primary shrink-0" /> {f}</li>)}
            </ul>
            {isScholar ? (
              <Button disabled className="w-full rounded-none h-11 bg-primary" data-testid="scholar-current">You're a Scholar 🎓</Button>
            ) : (
              <Button onClick={() => buy("scholar_monthly")} disabled={checkout.isPending} className="w-full rounded-none h-11 bg-primary hover:opacity-90" data-testid="subscribe-scholar-button">
                {checkout.isPending ? "Redirecting..." : "Become a Scholar"}
              </Button>
            )}
          </div>

          {/* Enterprise */}
          <div className="border border-border bg-card p-8" data-testid="tier-enterprise">
            <div className="flex items-center gap-2 mb-1"><Building2 className="h-5 w-5 text-primary" /><h3 className="font-display text-2xl font-bold">Enterprise</h3></div>
            <p className="text-sm text-muted-foreground">For teams & organizations</p>
            <div className="mt-4 mb-6"><span className="font-display text-4xl font-extrabold">Custom</span></div>
            <ul className="space-y-3 mb-8">
              {ENTERPRISE.map((f) => <li key={f} className="flex items-center gap-3 text-sm"><Check className="h-4 w-4 text-primary shrink-0" /> {f}</li>)}
            </ul>
            <Button onClick={() => navigate("/enterprise")} variant="outline" className="w-full rounded-none h-11" data-testid="contact-sales-button">
              Contact sales
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
